import PermissionButton from '../../permissions/PermissionButton';
import React, { useState } from 'react';
import { Card, Table, Input, Button, Space, Tabs, Popconfirm, message, Drawer, Modal  } from 'antd';
import { SearchOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined, ExclamationCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useFetchAllReglements, useDeleteReglement } from '../../hooks/regHooks';
import CreateRegWizard from './CreateRegWizard';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const ReglementList = ({ onSelectReglement, clientCodeFilter, onBackToDraft }) => {
  const { reglements, loading, error, refetch } = useFetchAllReglements();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [activeTab, setActiveTab] = useState('outstanding');
  const { handleDelete, isDeleting } = useDeleteReglement();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleReglementCreated = (reglementId) => {
    setIsDrawerOpen(false);
    onSelectReglement(reglementId); // This navigates to the new reglement
    refetch(); // Refresh the list
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Rechercher ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Rechercher
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const handleDeleteReglement = async (id) => {
    try {
      await handleDelete(id); // Use id instead of REF_REGV
      message.success('Règlement supprimé avec succès');
      refetch();
    } catch (err) {
      if (err.message.includes('Cannot delete reglement with existing details')) {
        Modal.error({
          title: 'Impossible de Supprimer le Règlement',
          content: (
            <div>
              <p>{err.message}</p>
              <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
                Veuillez supprimer tous les détails associés avant d'essayer de supprimer ce règlement.
              </p>
            </div>
          ),
          icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
          okText: 'Compris',
          maskClosable: true
        });
      } else {
        message.error('Échec de la suppression du règlement: Une erreur inattendue s\'est produite');
      }
    }
  };

  const getDateColumn = () => ({
    title: 'Date d\'Enregistrement',
    dataIndex: 'DATE_REG',
    key: 'DATE_REG',
    render: value => dayjs(value).format('DD/MM/YYYY'),
    sorter: (a, b) => dayjs(a.DATE_REG).unix() - dayjs(b.DATE_REG).unix(),
  });

  const getColumns = () => [
    getDateColumn(),
    {
      title: 'Référence',
      dataIndex: 'REF_REGV',
      key: 'REF_REGV',
      ...getColumnSearchProps('REF_REGV'),
    },
    {
      title: 'Compte',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
      ...getColumnSearchProps('COMPTE'),
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      ...getColumnSearchProps('CLIENT'),
    },
    {
      title: 'Montant',
      dataIndex: 'MNT_REGLER',
      key: 'MNT_REGLER',
      render: value => `${value?.toFixed(2)} MAD`,
      sorter: (a, b) => a.MNT_REGLER - b.MNT_REGLER,
    },
    {
      title: 'Restant',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: value => `${value?.toFixed(2)} MAD`,
      sorter: (a, b) => a.remainingAmount - b.remainingAmount,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="Êtes-vous sûr de vouloir supprimer ce règlement ?"
          onConfirm={() => handleDeleteReglement(record.id)} // Use id instead of REF_REGV
          okText="Oui"
          cancelText="Non"
          disabled={record.reglementdetails?.length > 0}
        >
          <PermissionButton permissionModule="payments" permissionAction="delete"
            icon={isDeleting ? <LoadingOutlined /> : <DeleteOutlined />} 
            danger
            disabled={isDeleting || record.reglementdetails?.length > 0}
            title={record.reglementdetails?.length > 0 ? 
              "Impossible de supprimer un règlement avec des détails existants" : 
              "Supprimer le règlement"}
          >
            Supprimer
          </PermissionButton>
        </Popconfirm>
      ),
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
    },
  ];

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };

  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const getFilteredReglements = () => {
    let filtered = reglements.filter(r => 
      activeTab === 'outstanding' ? (r.remainingAmount > 0) : (r.remainingAmount === 0)
    );
    
    // Apply client filter if provided
    if (clientCodeFilter) {
      filtered = filtered.filter(r => String(r.CODE_CLT) === String(clientCodeFilter));
    }
    
    return filtered;
  };

  return (
    <div>
      <Card style={tableCardStyle}>
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={titleStyle}>
            {clientCodeFilter ? `Règlements du Client (${clientCodeFilter})` : 'Liste des Règlements'}
          </h2>
          <Space>
            {onBackToDraft && (
              <Button 
                type="default" 
                icon={<ArrowLeftOutlined />}
                onClick={onBackToDraft}
              >
                Retour au Brouillon
              </Button>
            )}
            <PermissionButton permissionModule="payments" permissionAction="create"
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setIsDrawerOpen(true)}
            >
              Créer un Règlement
            </PermissionButton>
          </Space>
        </Space>
        
        <Tabs defaultActiveKey="outstanding" onChange={setActiveTab}>
          <TabPane tab="En Attente" key="outstanding" />
          <TabPane tab="Entièrement Réglés" key="fullySettled" />
        </Tabs>
        
        <Table
          columns={getColumns()}
          dataSource={getFilteredReglements()}
          rowKey="id" // Use id instead of REF_REGV
          pagination={{ pageSize: 10 }}
          onRow={(record) => ({
            onClick: () => onSelectReglement(record.id), // Use id instead of REF_REGV
          })}
          loading={loading}
        />

        <Drawer
          title="Créer un Nouveau Règlement"
          width={920}
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          styles={{
            body: {
              paddingBottom: 80,
            },
          }}
        >
          <CreateRegWizard 
            onCompleted={handleReglementCreated}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </Drawer>
      </Card>
    </div>
  );
};

export default ReglementList;